// ---------------------------------------------------------------------------
// The restart warning.
//
// Four visual states on one card:
//
//   0. PENDING — "An update is on its way. Active games will briefly pause."
//      No counter, because nothing knows the number yet. CI announces this when
//      a deploy starts; how long the build takes is not knowable up front.
//
//   1. COUNTDOWN — "Countdown is updating… 6s"
//      An info-toned card with a ticking monospace counter. This one is real:
//      it is broadcast by the process being replaced, from inside its own
//      SIGTERM handler, so the number is the actual time left. The previous
//      version predicted it in CI ninety seconds early, which is why it always
//      reached zero long before anything happened.
//
//   2. RESTARTING — "Restarting…"
//      The countdown hit zero and the new build has not answered yet. A subtle
//      pulse replaces the number.
//
//   3. SUCCESS — "✓ Updated successfully!" + Refresh button
//      `/api/config` returned a different buildId, which is the only thing that
//      actually demonstrates the new process is serving. A green card with a
//      refresh button, plus an auto-reload after three seconds in case the
//      player is mid-round and not watching.
//
// The card never auto-expires (duration: Infinity). It is dismissed by the
// linger timer, the success auto-dismiss, or the player tapping it.
//
// This component renders nothing itself. It is the seam between the store and
// the toast surface, and it lives outside the routes because the announcement
// usually arrives seconds before the socket drops: a player who backs out to
// the home screen in between should still be told why the server is about to go
// away.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { MAINTENANCE_TOAST_ID } from '../../lib/toast.js';
import { serverNow, useGameStore } from '../../store/gameStore.js';
import { ToastCard } from './Toasts.js';

/**
 * Stays up for a while after the announced moment: the restart itself is not
 * instant, and a notice that vanished exactly as the socket dropped would leave
 * the player with an unexplained disconnect.
 */
export const LINGER_MS = 90_000;

/**
 * How long a notice with no announced moment stays up.
 *
 * There is no deadline to linger past, so this is the only thing that will ever
 * take it away if the deploy is called off. It matches the server's own
 * `PENDING_GRACE_MS`, so a client that reloads mid-build is told the same thing
 * for the same length of time as one that did not.
 */
export const PENDING_LINGER_MS = 20 * 60_000;

/** How long the success card stays before auto-dismissing. */
const SUCCESS_DISMISS_MS = 30_000;

/** How long to wait before forcing a hard reload after success. */
const AUTO_RELOAD_MS = 3_000;

export function MaintenanceNotice() {
  const notice = useGameStore((store) => store.maintenance);
  const autoReloaded = useRef(false);

  useEffect(() => {
    if (notice === null) return;

    // --- Success state: the update landed and the new server is answering ---
    if (notice.updateComplete) {
      toast.custom(
        (t) => <SuccessCard t={t} />,
        { id: MAINTENANCE_TOAST_ID, duration: SUCCESS_DISMISS_MS },
      );

      // Auto-reload to pick up the new frontend bundle. Guarded so a
      // StrictMode double-run does not fire two reloads.
      if (!autoReloaded.current) {
        autoReloaded.current = true;
        const timer = window.setTimeout(() => {
          window.location.reload();
        }, AUTO_RELOAD_MS);
        return () => window.clearTimeout(timer);
      }
      return;
    }

    // --- Pending / Countdown / Restarting state ---
    const lingerLeft =
      notice.deployAt === null
        ? PENDING_LINGER_MS
        : notice.deployAt + LINGER_MS - serverNow();
    // An announcement that is already stale — a reload long after the deploy,
    // say — is not worth raising just to take it away again.
    if (lingerLeft <= 0) return;

    toast.custom(
      (t) => (
        <ToastCard t={t} tone="info" drain={false}>
          <CountdownContent message={notice.message} deployAt={notice.deployAt} />
        </ToastCard>
      ),
      // Deliberately never auto-dismissed, and deliberately one fixed id: a
      // second announcement replaces this card rather than stacking beside it.
      { id: MAINTENANCE_TOAST_ID, duration: Infinity },
    );

    const timer = window.setTimeout(() => toast.dismiss(MAINTENANCE_TOAST_ID), lingerLeft);
    return () => window.clearTimeout(timer);
  }, [notice]);

  return null;
}

// ---------------------------------------------------------------------------
// Countdown content — the message and a ticking counter.
// ---------------------------------------------------------------------------

function secondsUntil(deployAt: number): number {
  return Math.max(0, Math.round((deployAt - serverNow()) / 1000));
}

function CountdownContent({ message, deployAt }: { message: string; deployAt: number | null }) {
  const [, tick] = useState(0);

  useEffect(() => {
    // Nothing to count while there is no deadline, so nothing to re-render for.
    if (deployAt === null) return;
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [deployAt]);

  // No number rather than a guessed one. The message already says an update is
  // coming, which is the whole of what is known at this point.
  if (deployAt === null) return <span>{message}</span>;

  const remaining = secondsUntil(deployAt);
  return (
    <span className="flex flex-wrap items-baseline gap-x-2">
      <span>{message}</span>
      {remaining > 0 ? (
        <span className="tabular font-mono text-[13px] font-semibold text-accent">
          {remaining}s
        </span>
      ) : (
        <span className="font-mono text-[13px] font-semibold text-ink-muted animate-pulse">
          Restarting…
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Success card — green tone, refresh button, shown after reconnection.
// ---------------------------------------------------------------------------

function SuccessCard({ t }: { t: import('react-hot-toast').Toast }) {
  return (
    <ToastCard t={t} tone="good" drain={false} onClick={() => window.location.reload()}>
      <span className="flex items-center gap-3">
        <span className="min-w-0 flex-1">Updated successfully!</span>
        <span className="shrink-0 rounded-md bg-good/15 px-2.5 py-1 text-xs font-semibold text-good transition-colors group-hover:bg-good/25">
          Refresh
        </span>
      </span>
    </ToastCard>
  );
}
